import { defineMessage, defineMessages } from "react-intl";

export const shellMessages = defineMessages({
  skipToContent: { id: "shell.skipToContent", defaultMessage: "Skip to main content" },
  topBarLabel: { id: "shell.topBar.label", defaultMessage: "Explorer" },
  logo: { id: "shell.logo", defaultMessage: "S-Trust" },
  home: { id: "shell.home", defaultMessage: "Home" },
  notifications: { id: "shell.notifications", defaultMessage: "Notifications" },
  notificationsEmpty: {
    id: "shell.notifications.empty",
    defaultMessage: "You have no notifications.",
  },
  viewExports: { id: "shell.notifications.viewExports", defaultMessage: "View my exports" },
  apps: { id: "shell.apps", defaultMessage: "Apps" },
  appsExplorer: { id: "shell.apps.explorer", defaultMessage: "Explorer" },
  appsDocs: { id: "shell.apps.docs", defaultMessage: "Integration documentation" },
  appsDocsError: {
    id: "shell.apps.docs.error",
    defaultMessage: "We could not open the integration documentation. Try again.",
  },
  appsDocsLlms: { id: "shell.apps.docsLlms", defaultMessage: "Download llms-full.txt" },
  appsDocsLlmsError: {
    id: "shell.apps.docsLlms.error",
    defaultMessage: "We could not download the documentation. Try again.",
  },
  user: { id: "shell.user", defaultMessage: "User menu" },
  userNoRole: { id: "shell.user.noRole", defaultMessage: "No operator access" },
  rolePlatformAdministrator: {
    id: "shell.user.platformAdministrator",
    defaultMessage: "Platform Administrator",
  },
  roleViewer: { id: "shell.role.viewer", defaultMessage: "Viewer" },
  roleAnalyst: { id: "shell.role.analyst", defaultMessage: "Analyst" },
  roleAdministrator: { id: "shell.role.administrator", defaultMessage: "Administrator" },
  navLabel: { id: "shell.nav.label", defaultMessage: "Explorer sections" },
  navGroupInvestigate: { id: "shell.nav.group.investigate", defaultMessage: "Investigate" },
  navGroupAdminister: { id: "shell.nav.group.administer", defaultMessage: "Administer" },
  navGroupPlatform: { id: "shell.nav.group.platform", defaultMessage: "Platform" },
  productName: { id: "shell.productName", defaultMessage: "Explorer" },
  brandHome: { id: "shell.brandHome", defaultMessage: "S-Trust Explorer, home" },
  themeDark: { id: "shell.theme.dark", defaultMessage: "Dark" },
  themeLight: { id: "shell.theme.light", defaultMessage: "Light" },
  toastClose: { id: "shell.toast.close", defaultMessage: "Dismiss notification" },
  navSearch: { id: "shell.nav.search", defaultMessage: "Search" },
  navUsers: { id: "shell.nav.users", defaultMessage: "Transactions and users" },
  navBlocklist: { id: "shell.nav.blocklist", defaultMessage: "Blocklist" },
  navMap: { id: "shell.nav.map", defaultMessage: "Overview map" },
  navCollapse: { id: "shell.nav.collapse", defaultMessage: "Collapse side menu" },
  navExpand: { id: "shell.nav.expand", defaultMessage: "Expand side menu" },
  navPlatformOperators: { id: "shell.nav.platform.operators", defaultMessage: "Operators" },
  navPlatformOrganizations: {
    id: "shell.nav.platform.organizations",
    defaultMessage: "Organizations",
  },
  navPlatformAdministrators: {
    id: "shell.nav.platform.administrators",
    defaultMessage: "Platform Administrators",
  },
  navPlatformFleet: { id: "shell.nav.platform.fleet", defaultMessage: "Fleet" },
  crumbPlayers: { id: "shell.crumb.players", defaultMessage: "Players" },
  crumbDevices: { id: "shell.crumb.devices", defaultMessage: "Devices" },
  crumbTransactions: { id: "shell.crumb.transactions", defaultMessage: "Transactions" },
  crumbPlatform: { id: "shell.crumb.platform", defaultMessage: "Platform" },
  crumbAdminister: { id: "shell.crumb.administer", defaultMessage: "Administer" },
  settingsSaveFailed: {
    id: "shell.settings.saveFailed",
    defaultMessage: "We could not save your setting. It applies only to this session.",
  },
  meErrorTitle: { id: "shell.me.error.title", defaultMessage: "We could not load your profile" },
});

/** ICU messages with arguments are typed so callers must pass them. */
export const timeZoneMessage = defineMessage<{ timeZone: string }>(
  { id: "shell.timeZone.label", defaultMessage: "Displayed time zone: {timeZone}" },
  { typed: true },
);
export const userRoleMessage = defineMessage<{ role: string }>(
  { id: "shell.user.role", defaultMessage: "Role: {role}" },
  { typed: true },
);
/**
 * The installation's fixed environment (ADR 0011, design D7 of
 * bind-operator-environment-to-installation): the badge text, its screen-reader prefix and the
 * states around `GET /installation`.
 */
export const installationMessages = defineMessages({
  staging: { id: "shell.installation.badge.staging", defaultMessage: "Staging" },
  production: { id: "shell.installation.badge.production", defaultMessage: "Production" },
  badgeLabel: { id: "shell.installation.badgeLabel", defaultMessage: "Installation environment:" },
  loading: {
    id: "shell.installation.loading",
    defaultMessage: "Loading the installation environment",
  },
  errorTitle: {
    id: "shell.installation.error.title",
    defaultMessage: "We could not load this installation's environment",
  },
  errorDescription: {
    id: "shell.installation.error.description",
    defaultMessage:
      "The Explorer opens its screens only once it knows which environment this installation serves. Try again.",
  },
});

/** Said once when an old link carried `?environment=` naming the other installation. */
export const foreignLinkMessage = defineMessage<{ environment: string; current: string }>(
  {
    id: "shell.installation.foreignLink",
    defaultMessage:
      "This link was made for the {environment} installation. It opened here, in the {current} installation; use the {environment} Explorer to see that environment's data.",
  },
  { typed: true },
);

/** Sidebar footer, one mono line: the Explorer version and the installation's environment code. */
export const navFooterMessage = defineMessage<{ version: string; environment: string }>(
  { id: "shell.nav.footer", defaultMessage: "v{version} \u00b7 {environment}" },
  { typed: true },
);
export const languageChipMessage = defineMessage<{ locale: string }>(
  { id: "shell.language.chip", defaultMessage: "Language: {locale}" },
  { typed: true },
);
export const themeChipMessage = defineMessage<{ theme: string }>(
  { id: "shell.theme.chip", defaultMessage: "Theme: {theme}" },
  { typed: true },
);

export const notificationKindMessages = defineMessages({
  export_ready: {
    id: "shell.notifications.exportReady",
    defaultMessage: "Your export is ready to download.",
  },
  export_failed: {
    id: "shell.notifications.exportFailed",
    defaultMessage: "Your export failed. Request it again.",
  },
});

export const notificationsLabelMessage = defineMessage<{ count: number }>({
  id: "shell.notifications.unread",
  defaultMessage: "Notifications ({count, number} unread)",
});
